import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router'
import { registerRecoveryOtps } from '../api/registerRecoveryOtps'
import { revokeMFAMechanism as revokeApi } from '../api/revokeMFAMechanism'
import { apiRequest } from '../utils/apiRequest'
import { AvailableMFA, RECOVERY_OTP } from '../constants/mfa'
import { SETUP_MFA } from '../constants/routes'
import { isMFAEnabled } from '../utils/isMFAEnabled'

export function useSettings(initialSettings = {}) {
  const navigate = useNavigate()

  const [state, setState] = useState({
    isLocalLoginEnabled: initialSettings.isLocalLoginEnabled ?? false,
    enabledMFAMechanisms: initialSettings.enabledMFAMechanisms ?? [],
    mfaEnforced: initialSettings.mfaEnforced ?? false,
    hasMFAEnabled: initialSettings.hasMFAEnabled ?? false,
    isMFASetupInProgress: false,
    recoveryOTPRegisterErrorCode: null,
    revokeMFAErrorCode: null,
    mfaToRevoke: RECOVERY_OTP,
    isMFARevokeInProgress: false,
    isSuccessfullyRevoked: false,
    recoveryCodeList: null,
    isRecoveryCodesRenewInProgress: false,
    isPasswordSetupInProgress: false,
    passwordErrorCode: null,
  })

  const shouldSetupMFA = state.mfaEnforced && !state.hasMFAEnabled

  const hasAllMFAEnabled =
    state.enabledMFAMechanisms.length > 0 &&
    Object.keys(AvailableMFA).every((el) =>
      state.enabledMFAMechanisms.includes(el),
    )

  const setupMfa = useCallback(
    async ({ isFromSettings = true } = {}) => {
      setState((s) => ({ ...s, isMFASetupInProgress: true }))
      try {
        const { otps_list: recoveryCodeList } =
          await apiRequest(registerRecoveryOtps)
        navigate(SETUP_MFA, { state: { recoveryCodeList, isFromSettings } })
        setState((s) => ({ ...s, isMFASetupInProgress: false }))
      } catch (error) {
        setState((s) => ({
          ...s,
          recoveryOTPRegisterErrorCode: error.message,
          isMFASetupInProgress: false,
        }))
      }
    },
    [navigate],
  )

  const renewRecoveryCodes = useCallback(async () => {
    setState((s) => ({ ...s, isRecoveryCodesRenewInProgress: true }))
    try {
      const { otps_list: recoveryCodeList } =
        await apiRequest(registerRecoveryOtps)
      setState((s) => ({
        ...s,
        recoveryCodeList,
        isRecoveryCodesRenewInProgress: false,
      }))
    } catch (error) {
      setState((s) => ({
        ...s,
        recoveryOTPRegisterErrorCode: error.message,
        isRecoveryCodesRenewInProgress: false,
      }))
    }
  }, [])

  const addMechanism = useCallback((mfaMechanism) => {
    setState((s) => {
      const newMechanisms = [
        ...new Set([...s.enabledMFAMechanisms, mfaMechanism]),
      ]
      return {
        ...s,
        enabledMFAMechanisms: newMechanisms,
        hasMFAEnabled: isMFAEnabled(newMechanisms, s.isLocalLoginEnabled),
      }
    })
  }, [])

  const revokeMechanism = useCallback(
    async ({ mfaMechanism, password, recaptchaResponse } = {}) => {
      setState((s) => ({ ...s, isMFARevokeInProgress: true }))
      try {
        await apiRequest(revokeApi, {
          mfaMechanism: mfaMechanism || RECOVERY_OTP,
          password,
          recaptchaResponse,
        })
        setState((s) => ({
          ...s,
          isMFARevokeInProgress: false,
          isSuccessfullyRevoked: true,
          enabledMFAMechanisms: s.enabledMFAMechanisms.filter(
            (m) => m !== (mfaMechanism || RECOVERY_OTP),
          ),
        }))
      } catch (error) {
        setState((s) => ({
          ...s,
          isMFARevokeInProgress: false,
          revokeMFAErrorCode: error.code || error.message,
        }))
      }
    },
    [],
  )

  const revokeAllMechanisms = useCallback(
    async ({ password, recaptchaResponse } = {}) => {
      setState((s) => ({ ...s, isMFARevokeInProgress: true }))
      try {
        await apiRequest(revokeApi, {
          mfaMechanism: RECOVERY_OTP,
          password,
          recaptchaResponse,
        })
        setState((s) => ({
          ...s,
          isMFARevokeInProgress: false,
          isSuccessfullyRevoked: true,
          enabledMFAMechanisms: [],
        }))
      } catch (error) {
        setState((s) => ({
          ...s,
          isMFARevokeInProgress: false,
          revokeMFAErrorCode: error.code || error.message,
        }))
      }
    },
    [],
  )

  const selectMFAToRevoke = useCallback(
    (mfaToRevoke) =>
      setState((s) => ({ ...s, mfaToRevoke, revokeMFAErrorCode: null })),
    [],
  )

  const dismissRevokeConfirmation = useCallback(
    () =>
      setState((s) => ({
        ...s,
        isSuccessfullyRevoked: false,
        revokeMFAErrorCode: null,
        hasMFAEnabled: isMFAEnabled(
          s.enabledMFAMechanisms,
          s.isLocalLoginEnabled,
        ),
      })),
    [],
  )

  return {
    ...state,
    shouldSetupMFA,
    hasAllMFAEnabled,
    setupMfa,
    renewRecoveryCodes,
    addMechanism,
    revokeMechanism,
    revokeAllMechanisms,
    selectMFAToRevoke,
    dismissRevokeConfirmation,
  }
}
